/**
 * Штриховые коды на экране: Code 128 для баркода товара, QR для кода места.
 *
 * Обе функции ЧИСТЫЕ — «строка → геометрия», без DOM. Так сделано ради
 * проверки: код, который читает сканер, должен быть покрыт тестом, а вызов,
 * рисующий прямо в `<svg>`, тестом не покрывается ничем. Рисование остаётся
 * компоненту (`components/CodeSheet.tsx`), здесь только арифметика.
 *
 * Кодируем чужими энкодерами, а не своими: у Code 128 три набора символов,
 * автопереключение между ними и контрольная сумма по модулю 103, у QR —
 * коды Рида — Соломона. Своя реализация была бы кодом, который выглядит
 * рабочим, пока сканер не прочитает чужой товар.
 */

import { CODE128 } from "jsbarcode/bin/barcodes/CODE128";
import qrcode from "qrcode-generator";

/** Полоса кода: с какого модуля начинается и сколько модулей занимает. */
export type Bar = { x: number; width: number };

export type Barcode = {
  /** Всего модулей в ширину — он же `viewBox` рисунка. */
  modules: number;
  bars: Bar[];
};

/**
 * Code 128 для строки. `null` — «нарисовать нечем», а не пустая картинка.
 *
 * Пустая строка и строка вне ASCII 0-127 не кодируются вовсе, и показать
 * вместо них пустой прямоугольник значило бы предложить сканировать пустоту.
 * Отказ здесь возвращается значением, чтобы экран сказал словами, что кода
 * нет, — а не поймал исключение из глубины энкодера.
 */
export function code128(value: string): Barcode | null {
  const text = value.trim();
  if (!text) return null;
  let bits: string;
  try {
    const encoder = new CODE128(text, {});
    if (!encoder.valid()) return null;
    bits = encoder.encode().data;
  } catch {
    // Энкодер бросает на символах вне своего набора. Для склада это «такой
    // баркод нарисовать нельзя», а не сбой экрана.
    return null;
  }
  return { modules: bits.length, bars: runsOf(bits) };
}

/**
 * Подряд идущие «1» — одной полосой, а не прямоугольником на каждый модуль.
 *
 * Не оптимизация ради оптимизации: соседние прямоугольники браузер рисует со
 * щелью в доли пикселя, и сканер видит на месте широкой полосы две узкие.
 */
export function runsOf(bits: string): Bar[] {
  const bars: Bar[] = [];
  let start = -1;
  for (let i = 0; i <= bits.length; i++) {
    const dark = bits[i] === "1";
    if (dark && start < 0) start = i;
    if (!dark && start >= 0) {
      bars.push({ x: start, width: i - start });
      start = -1;
    }
  }
  return bars;
}

export type QrCode = {
  /** Сторона матрицы в модулях: 21 для короткого кода места, больше — длиннее. */
  size: number;
  /** Тёмные модули одним `path`: по прямоугольнику на модуль — 400+ узлов. */
  path: string;
};

/**
 * QR для строки. `null` — по той же причине, что и у Code 128.
 *
 * Уровень коррекции M, а не L: код места читают с экрана телефона под
 * складским светом, и запас в 15 % — разница между «считалось сразу» и
 * «поднесите ровнее». Версия выбирается энкодером сама (`0`) — прибитая
 * версия отказала бы на коде длиннее, чем сегодняшние двенадцать цифр.
 */
export function qr(value: string): QrCode | null {
  const text = value.trim();
  if (!text) return null;
  try {
    const code = qrcode(0, "M");
    code.addData(text);
    code.make();
    const size = code.getModuleCount();
    let path = "";
    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        if (code.isDark(row, col)) path += `M${col} ${row}h1v1h-1z`;
      }
    }
    return { size, path };
  } catch {
    return null;
  }
}
